import { useQuery } from '@apollo/client';
import { Devices, Event, PlayCircleOutlined, Timelapse } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LoadingPane } from '~/components/loading-pane';
import { AssetLink } from '../components/AssetLink';
import { AssetSummaryTable } from '../components/AssetSummaryTable';
import { ByEventCharts } from '../components/ByEventCharts';
import { PdfExportControls } from '../components/PdfExportControls';
import { StatCard } from '../components/StatCard';
import { ZoneAndDeviceGroupDonuts } from '../components/ZoneAndDeviceGroupDonuts';
import {
  CardPaper,
  EMPTY_STATS,
  SectionStack,
  TabRoot,
  avgPerEventPerDevice,
  formatMinutes,
} from '../lib';
import { PlayoutReportSponsorDetailDocument } from '../queries/PlayoutReportSponsorDetail.generated';
import { usePlayoutReport } from './context';

export const PlayoutReportsBySponsorDetail = () => {
  const { t } = useTranslation();
  const { sponsorId: sponsorIdParam } = useParams<{ sponsorId: string }>();
  const sponsorId = Number(sponsorIdParam);

  const { variables, querySkipped } = usePlayoutReport();

  const { data, loading } = useQuery(PlayoutReportSponsorDetailDocument, {
    skip: querySkipped || !Number.isFinite(sponsorId),
    fetchPolicy: 'network-only',
    variables: { ...variables, sponsorId },
  });

  const detail = data?.network?.playoutReport.sponsorDetail;
  const stats = detail?.stats ?? EMPTY_STATS;
  const sponsorName = detail?.sponsorName ?? '';
  const byAssetSummary = detail?.byAssetSummary ?? [];
  const byEvent = detail?.byEvent ?? [];
  const byZone = detail?.byZone ?? [];
  const byDeviceGroup = detail?.byDeviceGroup ?? [];

  const uniqueDevices = detail ? detail.stats.uniqueDevices : null;

  const avgPlaysPerDevice = avgPerEventPerDevice(stats.totalPlays, stats.eventCount, uniqueDevices);
  const avgDurationPerDevice = avgPerEventPerDevice(
    stats.totalDurationMs,
    stats.eventCount,
    uniqueDevices,
  );

  return (
    <TabRoot>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
          {detail ? (
            <AssetLink
              kind="sponsorApp"
              mediaItemId={null}
              contentItemId={Number.isFinite(sponsorId) ? sponsorId : null}
              contentFolderId={detail.contentFolderId ?? null}
              label={sponsorName || t('pages.playout_reports.by_sponsor.detail.unknown_sponsor')}
            />
          ) : (
            sponsorName || t('pages.playout_reports.by_sponsor.detail.unknown_sponsor')
          )}
        </Typography>
        {Number.isFinite(sponsorId) && (
          <PdfExportControls
            identifier={{ sponsorId }}
            name={sponsorName || t('pages.playout_reports.by_sponsor.detail.unknown_sponsor')}
            eventCount={byEvent.length}
            hasTableRows={byAssetSummary.length > 0}
          />
        )}
      </Box>

      <LoadingPane in={loading && !data} size={60} thickness={4} delay="0ms">
        {data && (
          <SectionStack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<PlayCircleOutlined />}
                  label={t('pages.playout_reports.stats.total_plays.label')}
                  value={
                    avgPlaysPerDevice != null
                      ? avgPlaysPerDevice.toLocaleString(undefined, { maximumFractionDigits: 1 })
                      : '—'
                  }
                  subtitle={t('pages.playout_reports.stats.total_plays.subtitle', {
                    total: stats.totalPlays.toLocaleString(),
                  })}
                  tooltip={t('pages.playout_reports.stats.total_plays.tooltip')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Timelapse />}
                  label={t('pages.playout_reports.stats.total_airtime.label')}
                  value={
                    avgDurationPerDevice != null
                      ? `${formatMinutes(avgDurationPerDevice).toLocaleString()} m`
                      : '—'
                  }
                  subtitle={t('pages.playout_reports.stats.total_airtime.subtitle', {
                    total: formatMinutes(stats.totalDurationMs).toLocaleString(),
                    hours: Math.round(stats.totalDurationMs / 3_600_000).toLocaleString(),
                  })}
                  tooltip={t('pages.playout_reports.stats.total_airtime.tooltip')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Event />}
                  label={t('pages.playout_reports.by_sponsor.detail.events_count.label')}
                  value={stats.eventCount.toLocaleString()}
                  tooltip={t('pages.playout_reports.by_sponsor.detail.events_count.tooltip')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Devices />}
                  label={t('pages.playout_reports.stats.devices_reached.label')}
                  value={uniqueDevices != null ? uniqueDevices.toLocaleString() : '—'}
                  subtitle={t('pages.playout_reports.stats.devices_reached.across_groups', {
                    count: stats.uniqueDeviceGroups,
                    groups: t('common.pluralize.device_group_plural', {
                      count: stats.uniqueDeviceGroups,
                    }),
                  })}
                  tooltip={t('pages.playout_reports.stats.devices_reached.tooltip')}
                />
              </Grid>
            </Grid>

            <ByEventCharts events={byEvent} />

            <ZoneAndDeviceGroupDonuts
              byZone={byZone}
              byDeviceGroup={byDeviceGroup}
              totalDurationMs={stats.totalDurationMs}
            />

            <CardPaper sx={{ pb: 0 }}>
              <Typography component="h2" variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {t('pages.playout_reports.by_asset.title')}
              </Typography>
              <AssetSummaryTable
                columnStateKey="sponsorDetailAssets"
                data={byAssetSummary}
                emptyLabel={t('pages.playout_reports.by_asset.empty')}
              />
            </CardPaper>
          </SectionStack>
        )}
      </LoadingPane>
    </TabRoot>
  );
};
